import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StarRating } from '@/components/ui/StarRating';
import { RankingBadge } from '@/components/ui/RankingBadge';
import { AmazonButton } from '@/components/ui/AmazonButton';
import { Product } from '@/types/database';

interface TopProductsProps {
  products: Product[];
  isLoading?: boolean;
}

export function TopProducts({ products, isLoading }: TopProductsProps) {
  // Demo products if none in database
  const displayProducts = products.length > 0 ? products : [
    {
      id: '1',
      name: 'Secretlab Titan Evo 2022',
      description: 'Silla gaming premium con soporte lumbar 4D',
      image_url: 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?w=300&h=300&fit=crop',
      amazon_url: 'https://amazon.es',
      price: 449.00,
      rating: 4.8,
      reviews_count: 3245,
      is_best_seller: true,
    },
    {
      id: '2',
      name: 'Herman Miller Aeron Remastered',
      description: 'La referencia en sillas ergonómicas de oficina',
      image_url: 'https://images.unsplash.com/photo-1580480055273-228ff5388ef8?w=300&h=300&fit=crop',
      amazon_url: 'https://amazon.es',
      price: 1395.00,
      rating: 4.9,
      reviews_count: 1876,
      is_best_seller: false,
    },
    {
      id: '3',
      name: 'IKEA Markus',
      description: 'Mejor relación calidad-precio del mercado',
      image_url: 'https://images.unsplash.com/photo-1589364203596-45afcc9e3b8a?w=300&h=300&fit=crop',
      amazon_url: 'https://amazon.es',
      price: 179.00,
      rating: 4.6,
      reviews_count: 8432,
      is_best_seller: true,
    },
    {
      id: '4',
      name: 'Autonomous ErgoChair Pro',
      description: 'Ergonómica completa con soporte lumbar ajustable',
      image_url: 'https://images.unsplash.com/photo-1519219788971-8d9797e0928e?w=300&h=300&fit=crop',
      amazon_url: 'https://amazon.es',
      price: 399.00,
      rating: 4.7,
      reviews_count: 2156,
      is_best_seller: true,
    },
  ] as Product[];

  if (isLoading) {
    return (
      <section className="bg-secondary/30 py-16">
        <div className="container-wide">
          <div className="mb-10">
            <h2 className="font-display text-3xl font-bold">Sillas Mejor Valoradas</h2>
            <p className="mt-2 text-muted-foreground">Las sillas más recomendadas este mes</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="mx-auto mb-4 h-32 w-32 rounded-lg bg-muted" />
                  <div className="h-5 rounded bg-muted" />
                  <div className="mt-2 h-4 w-3/4 rounded bg-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-secondary/30 py-16">
      <div className="container-wide">
        {/* Header */}
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-primary">Trending</span>
            </div>
            <h2 className="font-display text-3xl font-bold">Sillas Mejor Valoradas</h2>
            <p className="mt-2 text-muted-foreground">
              Las sillas más recomendadas este mes
            </p>
          </div>
          <Link to="/productos">
            <Button variant="ghost" className="gap-2">
              Ver todos <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Products Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {displayProducts.slice(0, 4).map((product, index) => (
            <Card key={product.id} className="card-hover group relative overflow-hidden">
              {/* Ranking Badge */}
              <div className="absolute left-3 top-3 z-10">
                <RankingBadge position={index + 1} />
              </div>

              {/* Best Seller Badge */}
              {product.is_best_seller && (
                <div className="absolute right-3 top-3 z-10">
                  <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                    Más vendido
                  </span>
                </div>
              )}

              <CardContent className="p-4">
                {/* Image */}
                <div className="mx-auto mb-4 flex h-32 w-32 items-center justify-center overflow-hidden rounded-lg bg-white p-2">
                  <img
                    src={product.image_url || 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=300&h=300&fit=crop'}
                    alt={product.name}
                    className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="mb-1 line-clamp-2 font-semibold transition-colors group-hover:text-primary">
                    {product.name}
                  </h3>
                  <p className="mb-2 line-clamp-1 text-sm text-muted-foreground">
                    {product.description}
                  </p>

                  {/* Rating */}
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <StarRating rating={product.rating || 0} size="sm" />
                    <span className="text-sm text-muted-foreground">
                      ({product.reviews_count?.toLocaleString('es-ES')})
                    </span>
                  </div>


                  {/* CTA */}
                  <AmazonButton href={product.amazon_url} size="sm" className="w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}